import { ApplicationType } from '@thesavingsgroup/enums/ApplicationType';
import { ChecklightSessionStatus } from '@thesavingsgroup/enums/ChecklightSessionStatus';
import { VehicleInformationType } from '@thesavingsgroup/enums/VehicleInformationType';
import i18n from 'i18next';
import { find, get as lodashGet, isEmpty, toString } from 'lodash';
import { StateCreator } from 'zustand';

import { inputEventHandler } from '../../utils/component/inputEventHandler';
import { DEFAULT_VEHICLE_APPLICATION_OWNER } from './constants/defaultVehicleApplicationOwner';
import { getVehicleApplicationDescription } from './functions/getVehicleApplicationDescription';
import { getVehicleApplicationType } from './functions/getVehicleApplicationType';
import { getVehicleBrand } from './functions/getVehicleBrand';
import { getVehicleDefaultImage } from './functions/getVehicleDefaultImage';
import { getVehicleDescription } from './functions/getVehicleDescription';
import { getVehicleInformationByType } from './functions/getVehicleInformationByType';
import { isVehicleApplicationInSubmission } from './functions/isVehicleApplicationInSubmission';
import { isVehicleApplicationRefandiType } from './functions/isVehicleApplicationRefandiType';
import { Application } from './types/Application';
import { DashboardState } from './types/DashboardState';
import { DashboardStateDependencies } from './types/DashboardStateDependencies';
import { DashboardVehicle } from './types/DashboardVehicle';

export const dashboardStore =
  ({
    useApiStore,
    useProgressStore,
    useTaskStore,
  }: DashboardStateDependencies): StateCreator<DashboardState> =>
  (set, get) => ({
    currentVehicle: null,
    fetchVehicleByApplicationAutopayNumberFailedMessage: null,
    fetchVehicleByIdFailedMessage: null,
    fetchVehiclesFailedMessage: null,
    fetchVehiclesSuccess: false,
    isLoadingTransition: false,
    vehicles: [],

    computed: {
      get isVehicleApplicationEmployeeMissingDetails() {
        const { currentVehicle } = get();

        const applicationOwnerFirstName: string = lodashGet(
          currentVehicle,
          'application.applicationOwner.firstName',
        );
        const applicationOwnerLastName: string = lodashGet(
          currentVehicle,
          'application.applicationOwner.lastName',
        );

        const applicationOwnerPhoneNumber = lodashGet(
          currentVehicle,
          'application.applicationOwner.phoneNumber',
        );

        const applicationOwnerEmail = lodashGet(
          currentVehicle,
          'application.applicationOwner.emailAddress',
        );

        return [
          applicationOwnerFirstName,
          applicationOwnerLastName,
          applicationOwnerPhoneNumber,
          applicationOwnerEmail,
        ].some(isEmpty);
      },
      get isVehicleConfirmed() {
        const { currentVehicle } = get();

        return lodashGet(currentVehicle, 'isConfirmed');
      },
      get isVehicleEmpty() {
        const { currentVehicle } = get();

        return isEmpty(currentVehicle);
      },
      get vehicleApplicationAutopayNumber() {
        const { currentVehicle } = get();

        return lodashGet(currentVehicle, 'application.autopayNumber');
      },
      get vehicleApplicationAutopayNumberTrimmed() {
        const {
          computed: { vehicleApplicationAutopayNumber },
        } = get();

        if (!vehicleApplicationAutopayNumber) {
          return '';
        }

        return vehicleApplicationAutopayNumber.substring(6);
      },
      get vehicleApplicationDescription() {
        const { currentVehicle } = get();

        return getVehicleApplicationDescription(currentVehicle);
      },
      get vehicleApplicationEmployeeEmail() {
        const {
          computed: {
            isVehicleApplicationEmployeeMissingDetails,
            vehicleBrand,
          },
          currentVehicle,
        } = get();

        if (isVehicleApplicationEmployeeMissingDetails) {
          return DEFAULT_VEHICLE_APPLICATION_OWNER[vehicleBrand].emailAddress;
        }

        return lodashGet(
          currentVehicle,
          'application.applicationOwner.emailAddress',
        );
      },
      get vehicleApplicationEmployeeName() {
        const {
          computed: {
            isVehicleApplicationEmployeeMissingDetails,
            vehicleBrand,
          },
          currentVehicle,
        } = get();

        if (isVehicleApplicationEmployeeMissingDetails) {
          return DEFAULT_VEHICLE_APPLICATION_OWNER[vehicleBrand].name;
        }

        const applicationOwnerFirstName: string = lodashGet(
          currentVehicle,
          'application.applicationOwner.firstName',
        );
        const applicationOwnerLastName: string = lodashGet(
          currentVehicle,
          'application.applicationOwner.lastName',
        );

        return `${applicationOwnerFirstName} ${applicationOwnerLastName.charAt(
          0,
        )}.`;
      },
      get vehicleApplicationEmployeePhone() {
        const {
          computed: {
            isVehicleApplicationEmployeeMissingDetails,
            vehicleBrand,
          },
          currentVehicle,
        } = get();

        if (isVehicleApplicationEmployeeMissingDetails) {
          return inputEventHandler.phoneNumber.formatter(
            DEFAULT_VEHICLE_APPLICATION_OWNER[vehicleBrand].phoneNumber,
          );
        }

        const applicationOwnerPhoneNumber = lodashGet(
          currentVehicle,
          'application.applicationOwner.phoneNumber',
        );

        return inputEventHandler.phoneNumber.formatter(
          applicationOwnerPhoneNumber,
        );
      },
      get vehicleApplicationHasCoapplicant() {
        const { currentVehicle } = get();

        return lodashGet(currentVehicle, 'application.hasCoApplicant');
      },
      get vehicleApplicationId() {
        const { currentVehicle } = get();

        return lodashGet(currentVehicle, 'application.id');
      },
      get vehicleApplicationInSubmission() {
        const { currentVehicle } = get();

        return isVehicleApplicationInSubmission(currentVehicle);
      },
      get vehicleApplicationIsFailedSubmission() {
        const {
          computed: { vehicleApplicationStatus },
        } = get();

        return (
          vehicleApplicationStatus === ChecklightSessionStatus.FAILED_SUBMISSION
        );
      },
      get vehicleApplicationIsLeaseBuyoutType() {
        const { currentVehicle } = get();

        return (
          lodashGet(currentVehicle, 'application.type') ===
          ApplicationType.LEASE_BUYOUT
        );
      },
      get vehicleApplicationIsQueuedForSubmission() {
        const {
          computed: { vehicleApplicationStatus },
        } = get();

        return (
          vehicleApplicationStatus ===
          ChecklightSessionStatus.QUEUED_FOR_SUBMISSION
        );
      },
      get vehicleApplicationIsRefandiType() {
        const { currentVehicle } = get();

        return isVehicleApplicationRefandiType(currentVehicle);
      },
      get vehicleApplicationIsRefinanceType() {
        const { currentVehicle } = get();

        return (
          lodashGet(currentVehicle, 'application.type') ===
          ApplicationType.REFINANCE
        );
      },
      get vehicleApplicationLeadChannelCode() {
        const { currentVehicle } = get();

        return lodashGet(currentVehicle, 'application.leadChannelCode');
      },
      get vehicleApplicationProgress() {
        const {
          computed: {
            vehicleApplicationSourceStatus,
            vehicleApplicationStatus,
          },
        } = get();

        // Prefer source status if available
        if (vehicleApplicationSourceStatus) {
          return useProgressStore
            .getState()
            .getStepsByApplicationStatus(vehicleApplicationSourceStatus);
        }

        return useProgressStore
          .getState()
          .getStepsByApplicationStatus(vehicleApplicationStatus);
      },
      get vehicleApplicationProgressPreview() {
        const {
          computed: {
            vehicleApplicationSourceStatus,
            vehicleApplicationStatus,
          },
        } = get();

        // Prefer source status if available
        if (vehicleApplicationSourceStatus) {
          return useProgressStore
            .getState()
            .getPreviewStepsByApplicationStatus(vehicleApplicationSourceStatus);
        }

        return useProgressStore
          .getState()
          .getPreviewStepsByApplicationStatus(vehicleApplicationStatus);
      },
      get vehicleApplicationSourceStatus() {
        const { currentVehicle } = get();

        return lodashGet(currentVehicle, 'application.sourceStatus');
      },
      get vehicleApplicationStatus() {
        const { currentVehicle } = get();

        return lodashGet(currentVehicle, 'application.status');
      },
      get vehicleApplicationTask() {
        const {
          computed: {
            vehicleApplicationSourceStatus,
            vehicleApplicationStatus,
          },
        } = get();
        const { getTaskByApplicationStatus } = useTaskStore.getState();

        // Prefer source status if available
        if (vehicleApplicationSourceStatus) {
          return getTaskByApplicationStatus(vehicleApplicationSourceStatus);
        }

        return getTaskByApplicationStatus(vehicleApplicationStatus);
      },
      get vehicleApplicationType() {
        const { currentVehicle } = get();

        return getVehicleApplicationType(currentVehicle);
      },
      get vehicleApplicationUploadedTypes() {
        const { currentVehicle } = get();

        return lodashGet(currentVehicle, 'application.verifications', []);
      },
      get vehicleBrand() {
        const { currentVehicle } = get();

        return getVehicleBrand(currentVehicle);
      },
      get vehicleDefaultImage() {
        const { currentVehicle } = get();

        if (!currentVehicle) {
          return null;
        }

        return getVehicleDefaultImage(currentVehicle);
      },
      get vehicleDescription() {
        const { currentVehicle } = get();

        return getVehicleDescription(currentVehicle);
      },
      get vehicleEmbeddedInsuranceInformation() {
        const { currentVehicle } = get();

        const information = lodashGet(currentVehicle, 'information');

        return getVehicleInformationByType(
          information,
          VehicleInformationType.EMBEDDED_INSURANCE,
        );
      },
      get vehicleEmbeddedInsuranceLeadId() {
        const {
          computed: { vehicleEmbeddedInsuranceInformation },
        } = get();

        return toString(
          lodashGet(vehicleEmbeddedInsuranceInformation, 'details.leadId'),
        );
      },
      get vehicleId() {
        const { currentVehicle } = get();

        return lodashGet(currentVehicle, 'id');
      },
      get vehiclesLoading() {
        const { vehicles, isLoadingTransition } = get();

        return isEmpty(vehicles) && isLoadingTransition;
      },
      get vehicleYearMakeModel() {
        const { currentVehicle } = get();

        if (
          !currentVehicle ||
          !currentVehicle.year ||
          !currentVehicle.make ||
          !currentVehicle.model
        ) {
          return null;
        }

        return `${currentVehicle.year} ${currentVehicle.make} ${currentVehicle.model}`;
      },
    },

    addVehicle(vehicle: DashboardVehicle | null) {
      if (!vehicle) {
        return;
      }

      const { vehicles } = get();

      set({ vehicles: [vehicle, ...vehicles] });
    },
    async fetchVehicleApplicationById(
      vehicleId: string,
      applicationId: string,
    ) {
      if (!vehicleId || !applicationId) {
        return;
      }

      const portalHttpRequest = useApiStore.getState().portalApi();

      return portalHttpRequest
        .get<never, Application>(
          `/2.0/vehicles/${vehicleId}/application/${applicationId}`,
        )
        .then((application) => application)
        .catch(() => null);
    },
    async fetchVehicleByApplicationAutopayNumber(autopayNumber: string) {
      if (!autopayNumber) {
        return;
      }

      set({
        currentVehicle: null,
        fetchVehicleByApplicationAutopayNumberFailedMessage: null,
      });

      const { fetchVehicles, setCurrentVehicle } = get();

      if (isEmpty(get().vehicles)) {
        await fetchVehicles();
      }

      const foundVehicle = find(
        get().vehicles,
        (vehicle) => vehicle.application?.autopayNumber === autopayNumber,
      );

      if (foundVehicle) {
        setCurrentVehicle(foundVehicle);
      } else {
        set({
          fetchVehicleByApplicationAutopayNumberFailedMessage: i18n.t(
            'errors.fetchVehicleByApplicationAutopayNumber.defaultMessage',
          ),
        });
      }
    },
    fetchVehicleById(vehicleId: string | undefined) {
      if (!vehicleId) {
        return;
      }

      set({
        currentVehicle: null,
        fetchVehicleByIdFailedMessage: null,
        isLoadingTransition: true,
      });

      const portalHttpRequest = useApiStore.getState().portalApi();

      return portalHttpRequest
        .get<never, DashboardVehicle>(`/2.0/vehicles/${vehicleId}`)
        .then((vehicle) => {
          set({ currentVehicle: vehicle });

          return null;
        })
        .catch((error) => {
          const errorStatus = toString(lodashGet(error, 'response.status'));

          const defaultMessage = i18n.t(
            'errors.fetchVehicleById.defaultMessage',
          );

          set({
            fetchVehicleByIdFailedMessage: lodashGet(
              i18n.t('errors.fetchVehicleById.statusMessage'),
              errorStatus,
              defaultMessage,
            ),
          });

          return null;
        })
        .finally(() => set({ isLoadingTransition: false }));
    },
    fetchVehicles() {
      const portalHttpRequest = useApiStore.getState().portalApi();

      set({
        fetchVehiclesFailedMessage: null,
        fetchVehiclesSuccess: false,
        isLoadingTransition: true,
      });

      return portalHttpRequest
        .get<never, DashboardVehicle[]>('/2.0/vehicles')
        .then((vehicles) => set({ fetchVehiclesSuccess: true, vehicles }))
        .catch((error) => {
          const errorStatus = toString(lodashGet(error, 'response.status'));

          const defaultMessage = i18n.t('errors.fetchVehicles.defaultMessage');

          set({
            fetchVehiclesFailedMessage: lodashGet(
              i18n.t('errors.fetchVehicles.statusMessage'),
              errorStatus,
              defaultMessage,
            ),
          });
        })
        .finally(() => set({ isLoadingTransition: false }));
    },
    setCurrentVehicle(vehicle: DashboardVehicle | null) {
      if (!vehicle) {
        return;
      }

      set({ currentVehicle: vehicle });
    },
  });
